<!-- W4-7 B08 机构财务 MVP（ADR-021 / B-FIN-01~02 子集） -->
<template>
  <div>
    <el-alert :closable="false" class="mb-16px" show-icon type="info">
      本页展示本机构<strong>师傅已完工</strong>订单收入（含待评价、已评价）；平台默认佣金率
      <strong>{{ commissionRateLabel }}</strong>（单笔以支付锁定快照为准）。
      结算台账按<strong>机构毛净</strong>；员工应得为内部分成预估，提现/真分账未开通。
    </el-alert>

    <!-- 收入概览 -->
    <el-row :gutter="16" class="mb-16px">
      <el-col :lg="8" :md="8" :sm="24" :xs="24">
        <el-card shadow="never">
          <div class="stat-title">今日 GMV</div>
          <div class="stat-value">¥{{ fmtMoney(summary?.todayGmv) }}</div>
          <div class="stat-sub">完成 {{ summary?.todayOrderCount ?? 0 }} 单</div>
        </el-card>
      </el-col>
      <el-col :lg="8" :md="8" :sm="24" :xs="24">
        <el-card shadow="never">
          <div class="stat-title">本周 GMV</div>
          <div class="stat-value">¥{{ fmtMoney(summary?.weekGmv) }}</div>
          <div class="stat-sub">完成 {{ summary?.weekOrderCount ?? 0 }} 单</div>
        </el-card>
      </el-col>
      <el-col :lg="8" :md="8" :sm="24" :xs="24">
        <el-card shadow="never">
          <div class="stat-title">本月 GMV</div>
          <div class="stat-value">¥{{ fmtMoney(summary?.monthGmv) }}</div>
          <div class="stat-sub">
            净收入 ¥{{ fmtMoney(summary?.monthNetIncome) }} · 完成 {{ summary?.monthOrderCount ?? 0 }} 单
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="mb-16px">
      <el-col :lg="12" :md="12" :sm="24" :xs="24">
        <el-card shadow="never">
          <template #header><span>结算概览（MVP 占位）</span></template>
          <el-descriptions :column="1" border>
            <el-descriptions-item label="待结算金额">
              ¥{{ fmtMoney(summary?.pendingSettlementAmount) }}
            </el-descriptions-item>
            <el-descriptions-item label="结算周期">T+1（对公打款 W5 开放）</el-descriptions-item>
          </el-descriptions>
          <el-button
            class="mt-12px"
            type="primary"
            :loading="settlementLoading"
            :disabled="!canApplySettlement"
            @click="onApplySettlement"
          >
            申请结算
          </el-button>
          <div v-if="settlementTip" class="stat-sub mt-8px">{{ settlementTip }}</div>
        </el-card>
      </el-col>
      <el-col :lg="12" :md="12" :sm="24" :xs="24">
        <el-card shadow="never">
          <template #header><span>承诺金</span></template>
          <el-descriptions v-if="summary?.deposit" :column="2" border>
            <el-descriptions-item label="应缴">¥{{ fmtMoney(summary.deposit.depositRequired) }}</el-descriptions-item>
            <el-descriptions-item label="已缴">¥{{ fmtMoney(summary.deposit.depositBalance) }}</el-descriptions-item>
            <el-descriptions-item label="可用">
              ¥{{ fmtMoney(summary.deposit.depositAvailable) }}
            </el-descriptions-item>
            <el-descriptions-item label="冻结">
              ¥{{ fmtMoney(summary.deposit.depositFrozen) }}
            </el-descriptions-item>
          </el-descriptions>
          <el-alert
            :closable="false"
            class="mt-12px"
            show-icon
            :title="
              summary?.depositReplenishTip ||
              '当前不支持在线缴纳保证金。请线下对公打款后联系平台运营确认入账；可用余额 > 0 后方可内派。'
            "
            type="info"
          />
        </el-card>
      </el-col>
    </el-row>

    <el-card class="mb-16px" shadow="never">
      <template #header><span>保证金流水</span></template>
      <el-table v-loading="depositLoading" :data="depositRecords" stripe>
        <el-table-column align="center" label="时间" prop="createTime" width="170" />
        <el-table-column align="center" label="类型" width="90">
          <template #default="{ row }">{{ depositTypeLabel(row.type) }}</template>
        </el-table-column>
        <el-table-column align="right" label="金额" width="110">
          <template #default="{ row }">
            <span :class="row.type === 'DEDUCT' ? 'text-danger' : 'text-success'">
              {{ row.type === 'DEDUCT' ? '-' : '+' }}¥{{ fmtMoney(row.amount) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column align="right" label="余额" width="110">
          <template #default="{ row }">¥{{ fmtMoney(row.balanceAfter) }}</template>
        </el-table-column>
        <el-table-column label="凭证/备注" min-width="160" show-overflow-tooltip>
          <template #default="{ row }">
            {{ [row.bizNo, row.remark].filter(Boolean).join(' · ') || '—' }}
          </template>
        </el-table-column>
      </el-table>
      <Pagination
        v-model:limit="depositQuery.pageSize"
        v-model:page="depositQuery.pageNo"
        :total="depositTotal"
        class="mt-12px"
        @pagination="loadDepositRecords"
      />
    </el-card>

    <el-card class="mb-16px" shadow="never">
      <template #header>
        <div class="flex items-center justify-between">
          <span>员工分成设置</span>
          <el-button link type="primary" @click="openShareEdit()">设置默认比例</el-button>
        </div>
      </template>
      <el-alert
        :closable="false"
        class="mb-12px"
        show-icon
        title="员工应得 = 机构毛净 × 分成比例；未配置则为 0。仅展示预估，不对员工自动打款。"
        type="info"
      />
      <el-table :data="shareRules" size="small" stripe>
        <el-table-column label="范围" min-width="160" prop="serviceName" />
        <el-table-column align="center" label="分成比例" width="120">
          <template #default="{ row }">{{ formatShare(row.shareRate) }}</template>
        </el-table-column>
        <el-table-column align="center" label="操作" width="120">
          <template #default="{ row }">
            <el-button link type="primary" @click="openShareEdit(row)">修改</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 收入明细 -->
    <el-card shadow="never">
      <template #header><span>收入明细（师傅完工）</span></template>
      <el-form ref="queryFormRef" :inline="true" :model="queryParams" class="-mb-15px" label-width="72px">
        <el-form-item label="订单号" prop="orderNo">
          <el-input
            v-model="queryParams.orderNo"
            class="!w-200px"
            clearable
            placeholder="订单号"
            @keyup.enter="handleQuery"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleQuery">
            <Icon class="mr-5px" icon="ep:search" />搜索
          </el-button>
          <el-button @click="resetQuery">
            <Icon class="mr-5px" icon="ep:refresh" />重置
          </el-button>
        </el-form-item>
      </el-form>

      <el-table v-loading="loading" :data="list" stripe>
        <el-table-column align="center" label="订单号" min-width="150" prop="orderNo" />
        <el-table-column label="服务" min-width="120" prop="serviceName" show-overflow-tooltip />
        <el-table-column align="center" label="师傅" prop="providerName" width="100" />
        <el-table-column
          :formatter="dateFormatter"
          align="center"
          label="完成时间"
          prop="finishTime"
          width="170"
        />
        <el-table-column align="right" label="订单金额" width="100">
          <template #default="{ row }">¥{{ fmtMoney(row.orderAmount) }}</template>
        </el-table-column>
        <el-table-column align="right" label="平台佣金" width="100">
          <template #default="{ row }">-¥{{ fmtMoney(row.commissionAmount) }}</template>
        </el-table-column>
        <el-table-column align="right" label="机构毛净" width="100">
          <template #default="{ row }">
            <span class="text-primary font-600">¥{{ fmtMoney(row.netIncome) }}</span>
          </template>
        </el-table-column>
        <el-table-column align="right" label="员工应得" width="100">
          <template #default="{ row }">¥{{ fmtMoney(row.employeeEarnAmount) }}</template>
        </el-table-column>
        <el-table-column align="right" label="机构留存" width="100">
          <template #default="{ row }">¥{{ fmtMoney(row.institutionKeepAmount) }}</template>
        </el-table-column>
      </el-table>

      <Pagination
        v-model:limit="queryParams.pageSize"
        v-model:page="queryParams.pageNo"
        :total="total"
        @pagination="getList"
      />
    </el-card>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import type { FormInstance } from 'element-plus'
import { dateFormatter } from '@/utils/formatTime'
import {
  applyO2oMerchantSettlement,
  getO2oEmployeeShareList,
  getO2oMerchantFinanceDepositRecords,
  getO2oMerchantFinanceIncomePage,
  getO2oMerchantFinanceSummary,
  saveO2oEmployeeShare,
  type O2oEmployeeShareRuleVO,
  type O2oFinanceDepositRecordVO,
  type O2oFinanceIncomeVO,
  type O2oFinanceSummaryVO
} from '@/api/merchant/o2o/finance'
import { ElMessage, ElMessageBox } from 'element-plus'

defineOptions({ name: 'MerchantO2oFinance' })

const summary = ref<O2oFinanceSummaryVO | null>(null)
const loading = ref(false)
const list = ref<O2oFinanceIncomeVO[]>([])
const total = ref(0)
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  orderNo: undefined as string | undefined
})
const queryFormRef = ref<FormInstance>()

const depositLoading = ref(false)
const depositRecords = ref<O2oFinanceDepositRecordVO[]>([])
const depositTotal = ref(0)
const depositQuery = reactive({ pageNo: 1, pageSize: 10 })

const commissionRateLabel = computed(() => {
  const rate = summary.value?.commissionRate
  if (rate == null) return '7%'
  return `${(Number(rate) * 100).toFixed(0)}%`
})

const shareRules = ref<O2oEmployeeShareRuleVO[]>([])

function formatShare(rate?: number | null) {
  if (rate == null) return '—'
  return `${(Number(rate) * 100).toFixed(2).replace(/\.?0+$/, '')}%`
}

async function loadShareRules() {
  shareRules.value = (await getO2oEmployeeShareList()) || []
}

async function openShareEdit(row?: O2oEmployeeShareRuleVO) {
  const isDefault = !row || row.institutionDefault || !row.catalogServiceId
  const current = row?.shareRate != null ? Number(row.shareRate) * 100 : 40
  try {
    const { value } = await ElMessageBox.prompt(
      isDefault ? '机构默认员工分成比例（%）' : `服务「${row?.serviceName}」分成比例（%）`,
      '员工分成',
      {
        inputValue: String(current),
        inputPattern: /^\d+(\.\d{1,2})?$/,
        inputErrorMessage: '请输入 0~100',
        confirmButtonText: '保存',
        cancelButtonText: '取消'
      }
    )
    const pct = Number(value)
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      ElMessage.warning('比例无效')
      return
    }
    await saveO2oEmployeeShare({
      catalogServiceId: isDefault ? 0 : row!.catalogServiceId,
      shareRate: Number((pct / 100).toFixed(4))
    })
    ElMessage.success('已保存')
    await loadShareRules()
  } catch {
    /* cancel */
  }
}

const settlementLoading = ref(false)
const settlementTip = ref('申请后进入 PENDING 台账，对公打款仍由运营线下处理。')
const canApplySettlement = computed(() => Number(summary.value?.pendingSettlementAmount || 0) > 0)

async function onApplySettlement() {
  const max = Number(summary.value?.pendingSettlementAmount || 0)
  if (max <= 0) {
    ElMessage.warning('暂无可结算金额')
    return
  }
  try {
    const { value } = await ElMessageBox.prompt(
      `请输入申请金额（元），不超过 ¥${fmtMoney(max)}`,
      '申请结算',
      {
        inputValue: String(max),
        inputPattern: /^\d+(\.\d{1,2})?$/,
        inputErrorMessage: '请输入有效金额',
        confirmButtonText: '提交申请',
        cancelButtonText: '取消'
      }
    )
    const amount = Number(value)
    if (!amount || amount <= 0 || amount > max) {
      ElMessage.warning('金额无效')
      return
    }
    settlementLoading.value = true
    await applyO2oMerchantSettlement({ amount, remark: 'B端申请结算' })
    ElMessage.success('已提交结算申请（台账）')
    await loadSummary()
  } catch {
    /* cancel */
  } finally {
    settlementLoading.value = false
  }
}

function fmtMoney(v?: number | null) {
  if (v == null || Number.isNaN(Number(v))) return '0.00'
  return Number(v).toFixed(2)
}

function depositTypeLabel(t?: string) {
  return t === 'RECHARGE' ? '入账' : t === 'DEDUCT' ? '扣减' : t === 'ADJUST' ? '调整' : t || '—'
}

async function loadSummary() {
  summary.value = await getO2oMerchantFinanceSummary()
}

async function loadDepositRecords() {
  depositLoading.value = true
  try {
    const data = await getO2oMerchantFinanceDepositRecords({ ...depositQuery })
    depositRecords.value = data?.list || []
    depositTotal.value = data?.total || 0
  } finally {
    depositLoading.value = false
  }
}

async function getList() {
  loading.value = true
  try {
    const data = await getO2oMerchantFinanceIncomePage({ ...queryParams })
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}

function handleQuery() {
  queryParams.pageNo = 1
  getList()
}

function resetQuery() {
  queryFormRef.value?.resetFields()
  handleQuery()
}

onMounted(async () => {
  await loadSummary()
  await loadDepositRecords()
  await loadShareRules()
  await getList()
})
</script>

<style scoped>
.stat-title {
  color: var(--el-text-color-secondary);
  font-size: 14px;
  margin-bottom: 8px;
}
.stat-value {
  font-size: 28px;
  font-weight: 600;
  line-height: 1.2;
}
.stat-sub {
  color: var(--el-text-color-secondary);
  font-size: 13px;
  margin-top: 8px;
}
</style>
